import React, { useState, useEffect } from "react";
import { ScopeSelect } from "./ScopeSelect";
import { useApp } from "../context/AppContext";
import { TEAMS, PROJECT_TYPES } from "../data/constants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Pencil, Lock, ShieldCheck } from "lucide-react";
import { StaffPicker, useStaffOptions } from "./StaffPicker";
import { CoStaffPicker, emptyCoStaff, dropPrimary } from "./CoStaffPicker";
import { toast } from "./ui/sonner";
import { addMonths } from "../lib/date";

const NATIVE_SEL = "mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60 disabled:cursor-not-allowed";
const DATE_ROLES = ["admin", "eng_manager", "ceo"];

/**
 * 项目基础资料编辑；footerActions 为详情页提供的项目管理入口，置于保存区之后。
 * 可选插槽让其他调用方保留原有表单，具体动作及权限由详情页维护。
 */
export default function EditProject({ project, open, onClose, footerActions }) {
  const { t, lang, user, api, refresh } = useApp();
  const zh = lang === "zh";
  const canDates = DATE_ROLES.includes(user.role);
  const canOrg = ["admin", "ceo"].includes(user.role) || user.role === "eng_manager";
  const staff = useStaffOptions();
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [autoEnd, setAutoEnd] = useState(false);
  useEffect(() => {
    if (project) {
      setForm({
        address: project.address || "", type: project.type || "ADU", clientName: project.clientName || "",
        phone: project.phone || "", email: project.email || "", manager: project.manager === "-" ? "" : (project.manager || ""),
        onlineManager: project.onlineManager || "",
        designer: project.designer || "", drafter: project.drafter || "",
        sales: project.sales || "",
        team: project.team || "", branch: project.branch || "",
        expectedStart: project.expectedStart || project.startDate || "", expectedEnd: project.expectedEnd || "",
        ownerLang: project.ownerLang || "zh",
        coStaff: { ...emptyCoStaff(), ...(project.coStaff || {}) },
      });
      setReason(""); setAutoEnd(false);
    }
  }, [project, open]);
  const [saving, setSaving] = useState(false);
  if (!project) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrimary = (k, v) => setForm((f) => ({ ...f, [k]: v, coStaff: dropPrimary(f.coStaff, k, v) }));
  const setCo = (k, arr) => setForm((f) => ({ ...f, coStaff: { ...f.coStaff, [k]: arr } }));
  const setStart = (v) => { setForm((f) => ({ ...f, expectedStart: v, expectedEnd: v ? addMonths(v, 6) : f.expectedEnd })); setAutoEnd(!!v); };

  const origStart = project.expectedStart || project.startDate || "";
  const origEnd = project.expectedEnd || "";
  const locked = !!origStart;
  const datesChanged = canDates && ((form.expectedStart || "") !== origStart || (form.expectedEnd || "") !== origEnd);
  const isAdmin = ["admin", "ceo"].includes(user.role);
  const needReason = datesChanged && locked && user.role !== "ceo";
  const needApproval = needReason && !isAdmin;

  const save = async () => {
    const patch = { ...form };
    if (!patch.manager) patch.manager = "-";
    if (canDates && datesChanged) { patch.startDate = form.expectedStart; if (needReason) patch.dateChangeReason = reason.trim(); }
    else { delete patch.expectedStart; delete patch.expectedEnd; }
    if (!canOrg) { delete patch.team; delete patch.branch; }
    if (needReason && !reason.trim()) { toast.error(zh ? "施工日期已设定，调整必须填写原因" : "Dates are set — a reason is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${project.id}`, patch);
      if (data?.dateChangeApplied === false) toast.success(zh ? "工期调整已提交 CEO 审批，批准后生效" : "Date change submitted for CEO approval");
      else if (data?.dateChangeApplied) toast.success((zh ? "工期已更新" : "Dates updated") + (data.dateChangeSynced?.length ? `：${data.dateChangeSynced.join("，")}` : ""));
      else toast.success(zh ? "已保存" : "Saved");
      await refresh();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    finally { setSaving(false); }
  };

  const L = ({ children }) => <label className="text-sm font-medium text-slate-700">{children}</label>;
  const branches = staff.branches?.length ? staff.branches : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="edit-project-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4 text-brand-500" />{zh ? "编辑项目信息" : "Edit Project"}</DialogTitle>
          <DialogDescription className="sr-only">{zh ? "修改项目基础信息与关键人员" : "Edit project info and key staff"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><L>{t("address")}</L><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1" data-testid="edit-project-address" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>{t("projectType")}</L>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1" data-testid="edit-project-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map((x) => <SelectItem key={x.key} value={x.key}>{zh ? x.zh : x.en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <L>{zh ? "业主首选语言" : "Owner language"}</L>
              <select value={form.ownerLang || "zh"} onChange={(e) => set("ownerLang", e.target.value)} data-testid="edit-project-owner-lang" data-no-i18n className={NATIVE_SEL}>
                <option value="zh">中文</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><L>{t("clientName")}</L><Input value={form.clientName} onChange={(e) => set("clientName", e.target.value)} className="mt-1" data-testid="edit-project-client" /></div>
            <div><L>{t("phone")}</L><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" data-testid="edit-project-phone" /></div>
          </div>
          <div><L>{t("email")}</L><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="edit-project-email" /></div>

          <div className="rounded-lg border border-slate-100 p-3 bg-slate-50/60 space-y-3" data-testid="edit-project-org">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-slate-700">{zh ? "所属分公司 / 施工组" : "Branch / Crew"}</span>
              {!canOrg && <span className="flex items-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" />{zh ? "仅工程部经理/IT管理员可改" : "Manager/Admin only"}</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>{zh ? "所属分公司" : "Branch"}</L>
                <ScopeSelect kind="region" value={form.branch || ""} onChange={(e) => set("branch", e.target.value)} disabled={!canOrg} data-testid="edit-project-branch" className={NATIVE_SEL}>
                  <option value="">{zh ? "未设置" : "Not set"}</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{zh ? b.zh : b.en}</option>)}
                </ScopeSelect>
              </div>
              <div>
                <L>{zh ? "施工组" : "Construction crew"}</L>
                <ScopeSelect kind="team" value={form.team || ""} onChange={(e) => set("team", e.target.value)} disabled={!canOrg} data-testid="edit-project-team" className={NATIVE_SEL}>
                  <option value="">{zh ? "未分配" : "Unassigned"}</option>
                  {TEAMS.map((x) => <option key={x.id} value={x.id}>{zh ? x.zh : x.name}</option>)}
                </ScopeSelect>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 p-3 space-y-3" data-testid="edit-project-staff">
            <p className="text-sm font-medium text-slate-700">{zh ? "关键人员（仅在职员工）" : "Key staff (active employees only)"}
              <span className="ml-1.5 text-[10px] font-normal text-slate-400">{zh ? "美国岗位可添加最多 3 名协办同事" : "US roles: up to 3 co-assist colleagues"}</span></p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>{zh ? "场地项目经理" : "Field PM"}</L>
                <StaffPicker value={form.manager} onChange={(v) => setPrimary("manager", v)} options={staff.pms} lang={lang} testid="edit-project-manager" placeholder={zh ? "选择项目经理" : "Select PM"} />
                <CoStaffPicker value={form.coStaff?.manager} onChange={(a) => setCo("manager", a)} options={staff.pms} primary={form.manager} lang={lang} testid="edit-project-co-manager" />
              </div>
              <div>
                <L>{zh ? "线上项目经理" : "Online PM"}</L>
                <StaffPicker value={form.onlineManager} onChange={(v) => setPrimary("onlineManager", v)} options={staff.pms} lang={lang} testid="edit-project-online-manager" placeholder={zh ? "选择线上项目经理" : "Select online PM"} />
                <CoStaffPicker value={form.coStaff?.onlineManager} onChange={(a) => setCo("onlineManager", a)} options={staff.pms} primary={form.onlineManager} lang={lang} testid="edit-project-co-online-manager" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>{zh ? "美国建筑设计师" : "US Architect"}</L>
                <StaffPicker value={form.designer} onChange={(v) => setPrimary("designer", v)} options={staff.architects} lang={lang} testid="edit-project-designer" placeholder={zh ? "选择美国建筑设计师" : "Select US architect"}
                  hint={zh ? "负责后期设计变更的跟进" : "Handles later design-change follow-ups"} />
                <CoStaffPicker value={form.coStaff?.designer} onChange={(a) => setCo("designer", a)} options={staff.architects} primary={form.designer} lang={lang} testid="edit-project-co-designer" />
              </div>
              <div>
                <L>{zh ? "中国建筑设计师" : "CN Architect"}</L>
                <StaffPicker value={form.drafter} onChange={(v) => set("drafter", v)} options={staff.drafters} lang={lang} testid="edit-project-drafter" placeholder={zh ? "选择中国建筑设计师" : "Select CN architect"} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>{t("salesperson")}</L>
                <StaffPicker value={form.sales} onChange={(v) => set("sales", v)} options={staff.sales} lang={lang} testid="edit-project-sales" placeholder={t("selectSales")} />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 p-3 bg-slate-50/60" data-testid="edit-project-dates">
            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
              <span className="text-sm font-medium text-slate-700">{zh ? "工期设置" : "Schedule Dates"}</span>
              {!canDates && <span className="flex items-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" />{zh ? "仅工程部经理/IT管理员可改" : "Manager/Admin only"}</span>}
              {canDates && locked && <span className="flex items-center gap-1 text-[11px] text-amber-600" data-testid="edit-project-dates-locked"><ShieldCheck className="h-3 w-3" />{user.role === "ceo" ? (zh ? "日期已设定，CEO 修改直接生效" : "Dates set — CEO edits apply directly") : isAdmin ? (zh ? "日期已设定，IT 管理员填写原因后直接生效（记入审计）" : "Dates set — admin edits apply directly with an audit note") : (zh ? "日期已设定，修改需填写原因并经 CEO 审批" : "Dates set — changes need a reason + CEO approval")}</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><L>{t("expectedStart")}</L><Input type="date" value={form.expectedStart} disabled={!canDates} onChange={(e) => setStart(e.target.value)} className="mt-1 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="edit-project-start" /></div>
              <div>
                <L>{t("expectedEnd")}</L>
                <Input type="date" value={form.expectedEnd} disabled={!canDates} onChange={(e) => { set("expectedEnd", e.target.value); setAutoEnd(false); }} className="mt-1 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="edit-project-end" />
                {autoEnd && <p className="text-[10px] text-brand-500 mt-0.5" data-testid="edit-project-end-auto">{zh ? "已按开工日 + 6 个月自动带出，可手动调整" : "Auto-set to start + 6 months; adjust if needed"}</p>}
              </div>
            </div>
            {needReason && (
              <div className="mt-3" data-testid="edit-project-reason-box">
                <L>{isAdmin ? (zh ? "调整原因（必填，记入审计并通知 PM / 工程经理）" : "Reason (required, recorded in audit)") : (zh ? "变更原因（必填，提交 CEO 审批）" : "Reason (required, sent to CEO)")}</L>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="edit-project-date-reason"
                  placeholder={zh ? "例如：业主要求延后 / 许可证延迟 / 材料到货延期…" : "e.g. owner request, permit delay, material lead time…"}
                  className="mt-1 w-full rounded-md border border-amber-300 bg-amber-50/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <p className="text-[10px] text-slate-400 mt-1">{isAdmin ? (zh ? "保存后立即生效并联动阶段起点 / 超期天数、市检预约、收款计划与提醒；同项目待批申请自动关闭。" : "Applies immediately and syncs phase start / overdue days, inspections, payment plan and reminders.") : (zh ? "提交后日期暂不改变；CEO 批准后自动生效并联动阶段起点、市检预约、收款计划与提醒。" : "Dates stay unchanged until the CEO approves; then the schedule, inspections, payment plan and reminders sync automatically.")}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="edit-project-cancel">{t("cancel")}</Button>
            <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="edit-project-save">{needApproval ? (zh ? "提交审批" : "Submit for approval") : t("save")}</Button>
          </div>
          {/* 低频管理操作沉底，与保存分隔，避免把删除误认为表单提交；窄屏允许自然换行。 */}
          {footerActions && <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4" data-testid="edit-project-actions">{footerActions}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
